import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { handoverService } from "../../services/api";
import moment from 'moment';
import { 
  FaTimes, FaCar, FaUser, FaCalendarAlt, FaGasPump, FaTachometerAlt,
  FaClipboardList, FaCheckCircle, FaEye, FaSearch, FaFilter, FaSort, FaChevronLeft, FaChevronRight
} from 'react-icons/fa';

const AllHandoversModal = ({ employeeId, onClose, onViewHandover, refreshCount = 0 }) => {

    console.log('AllHandoversModal rendered with employeeId:', employeeId);
  const [handovers, setHandovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    handoverType: '',
    status: '',
    startDate: '',
    endDate: '',
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  
  useEffect(() => {
    fetchHandovers();
  }, [employeeId, currentPage, filters, refreshCount]);
  
  const fetchHandovers = async () => {
    try {
      setLoading(true);
      console.log('Fetching handovers for employeeId:', employeeId, 'with filters:', filters);
      // Build query params
      const queryParams = {
        employee: employeeId,
        page: currentPage,
        limit: 10,
        ...filters
      };
      
      // Remove empty filters
      Object.keys(queryParams).forEach(key => 
        !queryParams[key] && delete queryParams[key]
      );
      
      const response = await handoverService.getHandovers(queryParams);

      console.log('Fetched handovers:', response.data.handovers);
      setHandovers(response.data.handovers);
      setTotalPages(response.data.pagination.pages);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching handovers:', err);
      setError('Failed to load handovers');
      setLoading(false);
    }
  };
  
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1); // Reset to first page when applying filters
    fetchHandovers();
    setIsFilterOpen(false);
  };
  
  const handleFilterReset = () => {
    setFilters({
      handoverType: '',
      status: '',
      startDate: '',
      endDate: '',
    });
    setCurrentPage(1);
  };
  
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-20 overflow-y-auto bg-black bg-opacity-60 flex justify-center items-center pt-4"
      style={{ backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <motion.div 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: -50, opacity: 0 }}
        className="bg-white w-full max-w-6xl rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto m-4"
        onClick={(e) => e.stopPropagation()} // Prevent clicks inside the modal from closing it
      >
        <div className="top-0 bottom-0 left-0 right-0 bg-white px-6 py-4 border-b flex justify-between items-center z-10">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <FaClipboardList className="mr-3 text-blue-600" />
            All Handovers
          </h2>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              className="px-4 py-2 bg-blue-100 text-blue-600 rounded-lg flex items-center hover:bg-blue-200 transition-colors"
            >
              <FaFilter className="mr-2" />
              Filters
            </button>
            <button 
              onClick={onClose} 
              className="p-2 bg-gray-200 hover:bg-gray-300 rounded-full transition-colors"
              aria-label="Close"
            >
              <FaTimes className="text-gray-700" />
            </button>
          </div>
        </div>
        
        {/* Filter Panel */}
        <AnimatePresence>
          {isFilterOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="border-b border-gray-200 overflow-hidden"
            >
              <form onSubmit={handleFilterSubmit} className="bg-gray-50 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Handover Type</label>
                    <select
                      name="handoverType"
                      value={filters.handoverType}
                      onChange={handleFilterChange}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Types</option>
                      <option value="pickup">Pickup</option>
                      <option value="return">Return</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                    <select
                      name="status"
                      value={filters.status}
                      onChange={handleFilterChange}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Statuses</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                      <option value="disputed">Disputed</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      name="startDate"
                      value={filters.startDate}
                      onChange={handleFilterChange}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      name="endDate"
                      value={filters.endDate}
                      onChange={handleFilterChange}
                      className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end mt-4 space-x-3">
                  <button
                    type="button"
                    onClick={handleFilterReset}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100"
                  >
                    Reset
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  >
                    Apply Filters
                  </button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
        
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
            <p className="ml-3 text-blue-600 font-medium">Loading handovers...</p>
          </div>
        ) : error ? (
          <div className="p-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-600 mb-4">
              <FaTimes className="text-2xl" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">Error Loading Handovers</h3>
            <p className="text-gray-600">{error}</p>
            <button
              onClick={fetchHandovers}
              className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        ) : handovers.length === 0 ? (
          <div className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gray-100 text-gray-400 mb-4">
              <FaClipboardList className="text-3xl" />
            </div>
            <h3 className="text-xl font-semibold text-gray-800 mb-2">No Handovers Found</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              There are no handovers matching your current filters. Try adjusting your search criteria.
            </p>
            {Object.values(filters).some(Boolean) && (
              <button
                onClick={handleFilterReset}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Clear Filters
              </button>
            )}
          </div>
        ) : (
          <div className="p-6">
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vehicle</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {handovers.map((handover, index) => (
                    <motion.tr 
                      key={handover._id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FaCalendarAlt className={`mr-2 ${handover.handoverType === 'pickup' ? 'text-green-500' : 'text-blue-500'}`} />
                          <span className="text-sm text-gray-900">
                            {moment(handover.handoverDate).format('DD/MM/YYYY HH:mm')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FaCar className={`mr-2 ${handover.handoverType === 'pickup' ? 'text-green-500' : 'text-blue-500'}`} />
                          <span className="text-sm text-gray-900">
                            {handover.vehicle?.make} {handover.vehicle?.model}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <FaUser className={`mr-2 ${handover.handoverType === 'pickup' ? 'text-green-500' : 'text-blue-500'}`} />
                          <span className="text-sm text-gray-900">{handover.customer.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          handover.handoverType === 'pickup' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {handover.handoverType === 'pickup' ? 'Pickup' : 'Return'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          handover.status === 'pending' 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : handover.status === 'completed'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                        }`}>
                          {handover.status === 'pending' ? 'Pending' : 
                           handover.status === 'completed' ? 'Completed' : 'Disputed'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <motion.button
                          onClick={() => onViewHandover(handover._id)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`${
                            handover.status === 'completed' 
                              ? 'bg-green-600 hover:bg-green-700' 
                              : 'bg-blue-600 hover:bg-blue-700'
                          } text-white py-2 px-4 rounded-lg text-sm flex items-center`}
                        >
                          <FaEye className="mr-1" />
                          View
                        </motion.button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center mt-6">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {totalPages}
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2 rounded-md border ${
                      currentPage === 1 
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <FaChevronLeft />
                  </button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    // Simple pagination logic to show 5 pages at most
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`w-10 h-10 rounded-md ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'border border-gray-300 text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-2 rounded-md border ${
                      currentPage === totalPages 
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <FaChevronRight />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default AllHandoversModal;
